import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Plus, Pipette } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// Predefined colors with their hex values
const PREDEFINED_COLORS = [
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Black', hex: '#000000' },
  { name: 'Grey', hex: '#808080' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'Gold', hex: '#FFD700' },
  { name: 'Blue', hex: '#0066CC' },
  { name: 'Navy', hex: '#000080' },
  { name: 'Light Blue', hex: '#87CEEB' },
  { name: 'Pink', hex: '#FFC0CB' },
  { name: 'Hot Pink', hex: '#FF69B4' },
  { name: 'Red', hex: '#FF0000' },
  { name: 'Burgundy', hex: '#800020' },
  { name: 'Purple', hex: '#800080' },
  { name: 'Lavender', hex: '#E6E6FA' },
  { name: 'Orange', hex: '#FFA500' },
  { name: 'Yellow', hex: '#FFFF00' },
  { name: 'Green', hex: '#008000' },
  { name: 'Lime', hex: '#32CD32' },
  { name: 'Teal', hex: '#008080' },
  { name: 'Turquoise', hex: '#40E0D0' },
  { name: 'Brown', hex: '#8B4513' },
  { name: 'Tan', hex: '#D2B48C' },
  { name: 'Beige', hex: '#F5F5DC' },
  { name: 'Coral', hex: '#FF7F50' },
  { name: 'Maroon', hex: '#800000' },
  { name: 'Olive', hex: '#808000' },
  { name: 'Charcoal', hex: '#36454F' },
  { name: 'Cream', hex: '#FFFDD0' },
  { name: 'Neon Green', hex: '#39FF14' },
  { name: 'Neon Pink', hex: '#FF6EC7' },
];

// Letter sizes
const LETTER_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];

// Numeric sizes (for footwear, etc.)
const NUMERIC_SIZES = ['4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16'];

// Volume sizes (for tanks, etc.)
const VOLUME_SIZES = ['7L', '8L', '10L', '12L', '15L', '18L'];

export const ColorPicker = ({ selectedColors = [], customColors = [], onColorsChange, onCustomColorsChange }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customHex, setCustomHex] = useState('#000000');

  const toggleColor = (colorName) => {
    if (selectedColors.includes(colorName)) {
      onColorsChange(selectedColors.filter(c => c !== colorName));
    } else {
      onColorsChange([...selectedColors, colorName]);
    }
  };

  const addCustomColor = () => {
    if (customName && customHex) {
      const newCustom = [...(customColors || []), { name: customName, hex: customHex }];
      onCustomColorsChange(newCustom);
      setCustomName('');
      setCustomHex('#000000');
      setShowCustom(false);
    }
  };

  const removeCustomColor = (index) => {
    const newCustom = customColors.filter((_, i) => i !== index);
    onCustomColorsChange(newCustom);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Available Colors</label>
      
      {/* Predefined Colors */}
      <div className="flex flex-wrap gap-2">
        {PREDEFINED_COLORS.map((color) => (
          <button
            key={color.name}
            type="button"
            onClick={() => toggleColor(color.name)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border transition-all ${
              selectedColors.includes(color.name)
                ? 'border-[#FF6B35] bg-[#FF6B35]/10 ring-2 ring-[#FF6B35]'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            title={color.name}
          >
            <span
              className="w-4 h-4 rounded-full border border-gray-300"
              style={{ backgroundColor: color.hex }}
            />
            <span className={selectedColors.includes(color.name) ? 'font-medium' : ''}>{color.name}</span>
          </button>
        ))}
      </div>

      {/* Custom Colors */}
      {customColors && customColors.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <span className="text-xs text-gray-500 w-full">Custom Colors:</span>
          {customColors.map((color, index) => (
            <div
              key={index}
              className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border border-[#FF6B35] bg-[#FF6B35]/10"
            >
              <span
                className="w-4 h-4 rounded-full border border-gray-300"
                style={{ backgroundColor: color.hex }}
              />
              <span>{color.name}</span>
              <button type="button" onClick={() => removeCustomColor(index)} className="text-red-500 hover:text-red-700">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Custom Color */}
      {showCustom ? (
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
          <input
            type="color"
            value={customHex}
            onChange={(e) => setCustomHex(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer"
          />
          <Input
            placeholder="Color name"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            className="flex-1 h-8 text-sm"
          />
          <Button type="button" size="sm" onClick={addCustomColor} className="h-8">Add</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setShowCustom(false)} className="h-8">Cancel</Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setShowCustom(true)} className="mt-2">
          <Pipette className="w-3 h-3 mr-1" /> Add Custom Color
        </Button>
      )}
    </div>
  );
};

export const SizePicker = ({ selectedSizes = [], sizeType = 'letter', onSizesChange, onSizeTypeChange }) => {
  const getSizeOptions = () => {
    switch (sizeType) {
      case 'numeric':
        return NUMERIC_SIZES;
      case 'volume':
        return VOLUME_SIZES;
      default:
        return LETTER_SIZES;
    }
  };

  const toggleSize = (size) => {
    if (selectedSizes.includes(size)) {
      onSizesChange(selectedSizes.filter(s => s !== size));
    } else {
      onSizesChange([...selectedSizes, size]);
    }
  };

  const selectAll = () => {
    onSizesChange(getSizeOptions());
  };

  const clearAll = () => {
    onSizesChange([]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Available Sizes</label>
        <Select value={sizeType || 'letter'} onValueChange={onSizeTypeChange}>
          <SelectTrigger className="w-36 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="letter">Letter (S, M, L)</SelectItem>
            <SelectItem value="numeric">Numeric (4, 5, 6)</SelectItem>
            <SelectItem value="volume">Volume (10L, 12L)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2">
        {getSizeOptions().map((size) => (
          <button
            key={size}
            type="button"
            onClick={() => toggleSize(size)}
            className={`px-3 py-1.5 rounded text-sm font-medium border transition-all ${
              selectedSizes.includes(size)
                ? 'border-[#0A2342] bg-[#0A2342] text-white'
                : 'border-gray-300 hover:border-gray-400 bg-white'
            }`}
          >
            {sizeType === 'numeric' ? `Size ${size}` : size}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={selectAll}>Select All</Button>
        <Button type="button" variant="outline" size="sm" onClick={clearAll}>Clear All</Button>
      </div>
    </div>
  );
};

// Component to display selected color/size on product cards
export const ColorSizeDisplay = ({ colors = [], customColors = [], sizes = [], sizeType = 'letter' }) => {
  const allColors = [...(colors || [])];
  
  if (!allColors.length && !sizes?.length) return null;

  return (
    <div className="space-y-2">
      {allColors.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {allColors.slice(0, 6).map((colorName) => {
            const predefined = PREDEFINED_COLORS.find(c => c.name === colorName);
            const custom = customColors?.find(c => c.name === colorName);
            const hex = predefined?.hex || custom?.hex || '#808080';
            return (
              <span
                key={colorName}
                className="w-5 h-5 rounded-full border border-gray-300"
                style={{ backgroundColor: hex }}
                title={colorName}
              />
            );
          })}
          {allColors.length > 6 && (
            <span className="text-xs text-gray-500">+{allColors.length - 6} more</span>
          )}
        </div>
      )}
      {sizes && sizes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {sizes.slice(0, 5).map((size) => (
            <Badge key={size} variant="outline" className="text-xs px-1.5 py-0">
              {sizeType === 'numeric' ? `${size}` : size}
            </Badge>
          ))}
          {sizes.length > 5 && (
            <span className="text-xs text-gray-500">+{sizes.length - 5} more</span>
          )}
        </div>
      )}
    </div>
  );
};

// Customer-facing selector for product pages
export const CustomerColorSizeSelector = ({ 
  colors = [], 
  customColors = [], 
  sizes = [], 
  sizeType = 'letter',
  selectedColor,
  selectedSize,
  onColorChange,
  onSizeChange 
}) => {
  return (
    <div className="space-y-4">
      {/* Color Selection - Swatches with ring selection indicator */}
      {colors && colors.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color: {selectedColor && <span className="font-semibold text-black">{selectedColor}</span>}
          </label>
          <div className="flex flex-wrap gap-3">
            {colors.map((colorName) => {
              const predefined = PREDEFINED_COLORS.find(c => c.name === colorName);
              const custom = customColors?.find(c => c.name === colorName);
              const hex = predefined?.hex || custom?.hex || '#808080';
              const isSelected = selectedColor === colorName;
              const isWhite = hex.toUpperCase() === '#FFFFFF' || hex.toUpperCase() === '#FFF';
              
              return (
                <button
                  key={colorName}
                  type="button"
                  onClick={() => onColorChange(colorName)}
                  className={`relative w-10 h-10 rounded-full transition-all ${
                    isSelected 
                      ? 'ring-2 ring-black ring-offset-2' 
                      : 'hover:ring-1 hover:ring-gray-400 hover:ring-offset-1'
                  }`}
                  style={{ 
                    backgroundColor: hex,
                    border: isWhite ? '1px solid #e5e7eb' : 'none'
                  }}
                  title={colorName}
                  data-testid={`color-swatch-${colorName.toLowerCase().replace(/\s+/g, '-')}`}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Size Selection - Dropdown or buttons */}
      {sizes && sizes.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Size: {selectedSize && <span className="font-semibold text-black">{sizeType === 'numeric' ? `Size ${selectedSize}` : selectedSize}</span>}
          </label>
          <div className="flex flex-wrap gap-2">
            {sizes.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => onSizeChange(size)}
                className={`px-4 py-2 rounded border text-sm font-medium transition-all ${
                  selectedSize === size
                    ? 'border-[#0A2342] bg-[#0A2342] text-white'
                    : 'border-gray-300 hover:border-gray-400 bg-white'
                }`}
                data-testid={`size-option-${size}`}
              >
                {sizeType === 'numeric' ? `Size ${size}` : size}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { PREDEFINED_COLORS, LETTER_SIZES, NUMERIC_SIZES, VOLUME_SIZES };
